/**
 * Pure formatting and sorting utilities for client portal billing data.
 * No FHIR dependencies — safe to import from any layer.
 */
import {
  coverageOrderToPriority as fhirCoverageOrderToPriority,
  priorityToCoverageOrder as fhirPriorityToCoverageOrder,
} from '@unify/fhir-model/resources/coverage';
import type {
  ClientPortalBillingDocument,
  ClientPortalBillingDocumentKind,
  ClientPortalBillingServiceLine,
  ClientPortalBillingStatus,
  ClientPortalInsurancePriority,
  ClientPortalInvoiceSummary,
} from './clientPortalFhirTypes';

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** Formats an ISO date string for display. Returns "—" for missing/invalid. */
export function formatBillingDate(iso?: string): string {
  if (!iso?.trim()) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

/** Formats a service period as "Jan 1 – Jan 31, 2026" or single date if start === end. */
export function formatServicePeriod(start?: string, end?: string): string {
  const s = start?.trim();
  const e = end?.trim();
  if (!s && !e) return '—';
  if (!s) return formatBillingDate(e);
  if (!e || s === e) return formatBillingDate(s);

  const ds = new Date(s);
  const de = new Date(e);
  if (Number.isNaN(ds.getTime()) || Number.isNaN(de.getTime())) return formatBillingDate(s);

  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  const fmtYear = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const sameYear = ds.getFullYear() === de.getFullYear();
  return sameYear
    ? `${fmt.format(ds)} – ${fmtYear.format(de)}`
    : `${fmtYear.format(ds)} – ${fmtYear.format(de)}`;
}

// ---------------------------------------------------------------------------
// Masking
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Invoice calculations
// ---------------------------------------------------------------------------

/** Returns the amount due: max(0, clientResponsibility - clientPaid). */
export function calculateInvoiceAmountDue(
  invoice: Pick<ClientPortalInvoiceSummary, 'clientResponsibilityCents' | 'clientPaidCents'>,
): number {
  return Math.max(0, invoice.clientResponsibilityCents - invoice.clientPaidCents);
}

/** Returns the client balance due for a service line. */
export function calculateServiceLineAmountDue(
  line: Pick<ClientPortalBillingServiceLine, 'clientResponsibilityCents' | 'clientPaidCents'>,
): number {
  if (line.clientResponsibilityCents == null) return 0;
  return Math.max(0, line.clientResponsibilityCents - (line.clientPaidCents ?? 0));
}

// ---------------------------------------------------------------------------
// Status labels and colors
// ---------------------------------------------------------------------------

const BILLING_STATUS_LABEL: Record<ClientPortalBillingStatus, string> = {
  due: 'Balance due',
  overdue: 'Overdue',
  paid: 'Paid',
  'partially-paid': 'Partially paid',
  processing: 'Processing',
  void: 'Void',
  refunded: 'Refunded',
  'insurance-pending': 'Insurance pending',
  unknown: 'Unknown',
};

export function billingStatusLabel(status: ClientPortalBillingStatus): string {
  return BILLING_STATUS_LABEL[status] ?? 'Unknown';
}

// ---------------------------------------------------------------------------
// Document kind labels
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/** Heuristic for assessment service labels when encounterKind is absent. */
export function looksLikeAssessmentServiceType(serviceType: string): boolean {
  const t = serviceType.trim().toLowerCase();
  if (!t) return false;
  if (t.includes('assessment')) return true;
  if (/\bphq-?9\b|\bgad-?7\b|\bphq-?a\b|\bscreening\b|\bquestionnaire\b/.test(t)) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Document grouping
// ---------------------------------------------------------------------------

/** Groups a flat document list into per-kind arrays. */
export function groupDocumentsByKind(
  docs: ClientPortalBillingDocument[],
): Record<ClientPortalBillingDocumentKind, ClientPortalBillingDocument[]> {
  const result: Record<ClientPortalBillingDocumentKind, ClientPortalBillingDocument[]> = {
    statement: [],
    superbill: [],
    eob: [],
    gfe: [],
    receipt: [],
    invoice: [],
    other: [],
  };
  for (const doc of docs) {
    result[doc.kind].push(doc);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Insurance priority slots (primary / secondary / tertiary)
// ---------------------------------------------------------------------------

/** Maps FHIR Coverage.order (1-based) to portal priority. */
export function coverageOrderToPriority(order?: number): ClientPortalInsurancePriority {
  return fhirCoverageOrderToPriority(order);
}

/** Maps portal priority to FHIR Coverage.order. */
export function priorityToCoverageOrder(priority: ClientPortalInsurancePriority): 1 | 2 | 3 {
  return fhirPriorityToCoverageOrder(priority);
}
